import ReportDocumentHeader from '@/components/Reports/ReportPrivate/_comons/ReportDocumentHeader';
import ReportDocumentSignature from '@/components/Reports/ReportPrivate/_comons/ReportDocumentSignature';
import { Group } from '@/components/_common';
import { useCompany } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { useDeliveryDetail } from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.hooks';
import {
  DeliveryReturnItem,
  ItemDeliveryDetailTypeEnum,
  ItemDeliveryDetailType,
} from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.types';
import { Card, Descriptions, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { Suspense, lazy, useEffect } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

import '../materialDeliveryReturn.style.less';
import formatterPrice from '@/utils/number/formatterPrice';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

type SearchParams = Record<'deliveryId', string | undefined>;

const date = moment();

const PrivateReportMaterialDelivery: React.FC = () => {
  const match = useRouteMatch();
  const { search } = useLocation();
  const params = Object.fromEntries(
    new URLSearchParams(search),
  ) as SearchParams;

  const { data, isLoading } = useDeliveryDetail(params.deliveryId || undefined);

  const { data: company } = useCompany(data?.result?.companyId);

  const columns: ColumnsType<DeliveryReturnItem> = [
    {
      dataIndex: 'complementIdentifier',
      title: 'OS',
      ellipsis: true,
      width: '10%',
    },
    {
      dataIndex: 'description',
      title: 'Item',
      ellipsis: true,
      width: '20%',
    },
    {
      dataIndex: 'manufacturingCode',
      title: 'Cód. Fabricante',
      ellipsis: true,
      width: '20%',
    },
    {
      dataIndex: 'type',
      title: 'Tipo',
      ellipsis: true,
      width: '10%',
      render: (type: ItemDeliveryDetailTypeEnum) =>
        ItemDeliveryDetailType[type],
    },
    {
      dataIndex: 'costPrice',
      title: 'Custo ($)',
      ellipsis: true,
      width: '15%',
      align: 'right',
      render: (value: number) =>
        typeof value === 'number' ? formatterPrice(value) : '-',
    },
    {
      dataIndex: 'requestQuantity',
      title: 'Qtde. Solic.',
      ellipsis: true,
      width: '10%',
      align: 'right',
      render: (value: number) => formatterPrice(value),
    },
    {
      dataIndex: 'deliveredQuantity',
      title: 'Qtde. Entr.',
      ellipsis: true,
      width: '10%',
      align: 'right',
      render: (value: number) => formatterPrice(value),
    },
  ];

  useEffect(() => {
    if (match.isExact && !isLoading && !!data && !!company) {
      (async () => {
        if (company.logo) {
          await fetch(company.logo);
        }
        setTimeout(() => window.print(), 1000);
      })();
    }
  }, [match.isExact, isLoading, data, company]);

  return (
    <Suspense fallback={<>Carregando...</>}>
      {match.isExact && !isLoading && !!data && !!company && (
        <TemplateReport>
          <main className="material-delivery-return">
            <Space direction="vertical" size="large" className="w-100">
              <ReportDocumentHeader
                company={{ name: company.name, logo: company.logo }}
                title="Entrega de material"
                date={date}
              />

              <Card>
                <Descriptions column={3}>
                  <Descriptions.Item
                    label="Entregue por"
                    labelStyle={{ fontWeight: 'bold' }}
                    style={{ padding: 0 }}
                  >
                    {data?.result.deliveryManName}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label="Recebido por"
                    labelStyle={{ fontWeight: 'bold' }}
                    style={{ padding: 0 }}
                  >
                    {data?.result.receivedManName}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label="Data"
                    labelStyle={{ fontWeight: 'bold' }}
                    style={{ padding: 0 }}
                  >
                    {moment(data?.result.deliveredDate).format('DD/MM/YYYY')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card>
                <Table
                  columns={columns}
                  dataSource={data?.result.itemDeliveryDetail.map(
                    (item, key) => ({
                      ...item,
                      key,
                    }),
                  )}
                  scroll={{ x: true }}
                  pagination={false}
                />
              </Card>

              <Group position="center">
                <div className="w-50" style={{ marginTop: 40 }}>
                  <ReportDocumentSignature date={date} />
                </div>
              </Group>
            </Space>
          </main>
        </TemplateReport>
      )}
    </Suspense>
  );
};

export default PrivateReportMaterialDelivery;
